import { useState } from "react";
import { API_BASE_URL } from "../utils/constants.js";

// Backend constants from vitros-pure/mathlib/AnalyzerSource/appsrvcs/include/CP_ArraySizes.h
//   CP_MAX_NUM_REPS_PER_CAL = 100
// VitrosService.cpp parses aadResponse1/2 row-major filling [level][rep] until input
// is exhausted, so each level slot must contain exactly MAX_REPS_PER_LEVEL values
// (zero-padded). See VitrosService.cpp:400-415.
const MAX_REPS_PER_LEVEL = 100;
const MAX_LEVELS = 10;

// Per-level / per-rep range gates the legacy DMController initialises to "no limit"
// (10000.0) but that VitrosService.cpp's calibrate handler leaves at the memset(0)
// default.  Without these the math sees "max allowed delta between reps = 0" and
// rejects every level, returning A0..A3 = 0 with condition code U90-021 / error
// bit 0x20.  The frontend supplies the same defaults DMController.cs does
// (`input.afCalRepRangeResp1 = InitArray(MS_MAX_LEVELS, 10000.0f);` etc.).
const NO_RANGE_LIMIT = 10000.0;
const REPS_NO_LIMIT_2D = () => new Array(MAX_LEVELS * MAX_REPS_PER_LEVEL).fill(NO_RANGE_LIMIT);
const LEVELS_NO_LIMIT = () => new Array(MAX_LEVELS).fill(NO_RANGE_LIMIT);
const LEVELS_ZERO = () => new Array(MAX_LEVELS).fill(0);

// "Ignore none" sentinel for sCalLevelToIgnore. VitrosService.cpp defaults to 0
// when the field is absent, which means "ignore the FIRST cal level" - silently
// dropping SAV[0] from the fit. DMController.cs uses 255 (= "none").
const CAL_LEVEL_IGNORE_NONE = 255;

/**
 * useMicroSlideApi
 *
 * Hook that wraps the three MicroSlide backend endpoints exposed by
 * vitros-pure/web/server.cpp:
 *   POST /api/v1/microslide/response
 *   POST /api/v1/microslide/calibrate
 *   POST /api/v1/microslide/predict
 *
 * Mirrors the shape of useMicroTipApi so the layout components can be
 * structurally identical to MicroTip.
 */
export default function useMicroSlideApi(parameters = {}, rows = [], extras = {}) {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ------------------------------------------------------------------
    // Helpers
    // ------------------------------------------------------------------
    const num = (v, def = 0) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : def;
    };

    const buildSpline = (s) => {
        if (!s || !Array.isArray(s.nodes) || s.nodes.length === 0) {
            return { sSplineNodeCount: 0, afX: [], afY1: [], afY2: [] };
        }
        return {
            sSplineNodeCount: s.nodes.length,
            afX: s.nodes.map(n => num(n.x)),
            afY1: s.nodes.map(n => num(n.y)),
            afY2: s.nodes.map(n => num(n.y2))
        };
    };

    const buildAssay = () => ({
        eAssayModelType: num(parameters?.eAssayModelType, 1),
        bIncreasingRate: num(parameters?.bIncreasingRate, 1),
        bDrToDtTransformFlag: num(parameters?.bDrToDtTransformFlag),
        eSubDepMethodType: num(parameters?.eSubDepMethodType, 1),
        fInductionTime: num(parameters?.fInductionTime),
        fSDTLimit: num(parameters?.fSDTLimit),
        fSpikeDerivTolerance: num(parameters?.fSpikeDerivTolerance),
        fInsideOutThreshold: num(parameters?.fInsideOutThreshold),
        fSubDepTime1: num(parameters?.fSubDepTime1),
        fSubDepTime2: num(parameters?.fSubDepTime2),
        sMinPointsInWindow: num(parameters?.sMinPointsInWindow),
        fFirstDerivTolerance: num(parameters?.fFirstDerivTolerance),
        eIRAlgorithm: num(parameters?.eIRAlgorithm),
        eRegressionMethodType: num(parameters?.eRegressionMethodType),
        sWindowModulator: num(parameters?.sWindowModulator),
        fWindowLengthMax: num(parameters?.fWindowLengthMax),
        fWindowLengthMin: num(parameters?.fWindowLengthMin),
        fLowRateLongWindowValue: num(parameters?.fLowRateLongWindowValue),
        fHighRateShortWindowValue: num(parameters?.fHighRateShortWindowValue),
        fMWTCurveDampeningConstant: num(parameters?.fMWTCurveDampeningConstant),
        fMWTSpikeTolerance: num(parameters?.fMWTSpikeTolerance),
        fCMSpikeLimitB0: num(parameters?.fCMSpikeLimitB0),
        fCMSpikeLimitB1: num(parameters?.fCMSpikeLimitB1),
        fCMSpikeLimitB2: num(parameters?.fCMSpikeLimitB2),
        dPredictTime1: num(parameters?.dPredictTime1),
        dPredictTime2: num(parameters?.dPredictTime2),
        fADToMVConversionFactor: num(parameters?.fADToMVConversionFactor),
        sTimeTemplateSize: num(parameters?.sTimeTemplateSize),
        fExtrapInductionLimit: num(parameters?.fExtrapInductionLimit),
        fExtrapSlopeLimit: num(parameters?.fExtrapSlopeLimit),
        fReportableRangeLow: num(parameters?.fReportableRangeLow),
        fReportableRangeHigh: num(parameters?.fReportableRangeHigh, 1000),
        fKExponent: num(parameters?.fKExponent),
        bLogarithmicKModel: num(parameters?.bLogarithmicKModel),
        eMonotonicityFlag: num(parameters?.eMonotonicityFlag),

        xPreprocessingSpline: buildSpline(extras?.splines?.preprocessing),
        xG1Spline: buildSpline(extras?.splines?.g1),
        xG2Spline: buildSpline(extras?.splines?.g2),
        xG3Spline: buildSpline(extras?.splines?.g3),
        xG4Spline: buildSpline(extras?.splines?.g4),
        fExtremaCheck1: num(parameters?.fExtremaCheck1),
        fExtremaCheck2: num(parameters?.fExtremaCheck2),
    });

    // ------------------------------------------------------------------
    // Payload builders
    // ------------------------------------------------------------------
    const buildResponsePayload = () => {
        const cleanRows = (rows || []).filter(r =>
            r && (r.signal !== "" || r.reference !== "" || r.dark !== "" || r.time !== "")
        );

        // DM (dual wavelength) needs one correction factor per filter. The
        // math reads adWhiteCorrFactor[FilterIndex] / adBlackCorrFactor[FilterIndex]
        // when computing each filter's reflectance, so sending a single-element
        // array would leave WCF[1] / BCF[1] = 0 and trigger U90-295
        // (CP_BAD_REFLECTANCE_ERR) on the second-filter read. Accept a
        // comma-separated list ("0.9894, 0.9712") for per-filter values, or a
        // single value that gets broadcast to both wavelengths.
        const expandPerFilter = (raw, fallback) => {
            const list = parseReps(raw);
            if (list.length === 0) return [fallback, fallback];
            if (list.length === 1) return [list[0], list[0]];
            return list;
        };

        return {
            read: {
                iLevel: num(extras?.iLevel),
                iRep: num(extras?.iRep),
                sTotalNumberOfReads: cleanRows.length,
                aiSignalReading: cleanRows.map(r => num(r.signal)),
                aiReferenceReading: cleanRows.map(r => num(r.reference)),
                aiDarkReading: cleanRows.map(r => num(r.dark)),
                adReadTimeInSeconds: cleanRows.map(r => num(r.time)),
                adWhiteCorrFactor: expandPerFilter(extras?.wcf, 1),
                adBlackCorrFactor: expandPerFilter(extras?.bcf, 0)
            },
            assay: buildAssay()
        };
    };

    // Parse a cell that may contain multiple replicate values.
    // Accepts arrays, comma/space/tab/newline separated strings, or single numbers.
    const parseReps = (v) => {
        if (v === null || v === undefined || v === "") return [];
        if (Array.isArray(v)) return v.map(x => num(x));
        if (typeof v === "string") {
            return v.split(/[\s,]+/).filter(s => s !== "").map(s => num(s));
        }
        return [num(v)];
    };

    const buildCalibratePayload = () => {
        const assay = buildAssay();

        // Only rows having at least one response
        const cleanRows = (rows || []).filter(
            r =>
                r &&
                (
                    (r.resp1 !== "" && r.resp1 !== undefined) ||
                    (r.resp2 !== "" && r.resp2 !== undefined)
                )
        );

        const aadResponse1 = [];
        const aadResponse2 = [];
        const asNumberCalReps = [];
        const afSAV = [];

        cleanRows.forEach(r => {
            const r1 = parseReps(r.resp1);
            const r2 = parseReps(r.resp2);

            const repCount = Math.max(r1.length, r2.length);

            asNumberCalReps.push(repCount);
            afSAV.push(num(r.sav));

            // 100 responses per calibration level
            const levelResp1 = new Array(MAX_REPS_PER_LEVEL).fill(0);
            const levelResp2 = new Array(MAX_REPS_PER_LEVEL).fill(0);

            for (let i = 0; i < Math.min(repCount, MAX_REPS_PER_LEVEL); i++) {
                levelResp1[i] = r1[i] ?? 0;
                levelResp2[i] = r2[i] ?? 0;
            }

            aadResponse1.push(...levelResp1);
            aadResponse2.push(...levelResp2);
        });

        return {
            ...assay,

            sNumberCalLevels: cleanRows.length,

            // Example: [3,3,3]
            asNumberCalReps,

            // Example: [15.5,103.7,259.4]
            afSAV,

            // 100 values per calibration level
            aadResponse1,
            aadResponse2,

            // Calibration defaults
            sCalLevelToIgnore: CAL_LEVEL_IGNORE_NONE,

            afCalRepRangeResp1: LEVELS_NO_LIMIT(),
            afCalRepRangeResp2: LEVELS_NO_LIMIT(),

            aadSubDepCheckValue: REPS_NO_LIMIT_2D(),
            aadSubDepDeltaCheckValue: REPS_NO_LIMIT_2D(),

            afBlankSAV: LEVELS_ZERO(),
            afMWTTolerance: LEVELS_ZERO(),
            afSubDepMultiplier: LEVELS_ZERO(),
            afKinErrFirstPtMult: LEVELS_ZERO(),

            fInitalValueForBlank: 0,
            fConvergenceTolerFactor: 0,

            bPerformCalQualityChecks: 0,
            bCalBottleOrderCheckNeeded: 0,

            // Use parameter values if available
            fExtremaCheck1: num(parameters?.fExtremaCheck1),
            fExtremaCheck2: num(parameters?.fExtremaCheck2)
        };
    };

    const buildUnitTestCalibratePayload = () => ({
        sNumberCalLevels: 3,
        asNumberCalReps: [3, 3, 3],

        eAssayModelType: 0,
        bIncreasingRate: 1,
        eMonotonicityFlag: 0,

        afSAV: [15.5, 103.7, 259.4],

        fKExponent: 2.0,
        bLogarithmicKModel: 0,

        sCalLevelToIgnore: 255,
        fInitalValueForBlank: 0.0,
        fConvergenceTolerFactor: 0.0,

        bPerformCalQualityChecks: 0,
        bCalBottleOrderCheckNeeded: 0,

        fExtremaCheck1: 0.58444,
        fExtremaCheck2: -8.44835,

        afCalRepRangeResp1: Array(10).fill(10000),
        afCalRepRangeResp2: Array(10).fill(10000),

        aadResponse1: [
            1.332686, 1.332686, 1.332686,
            ...Array(97).fill(0),

            1.156716833, 1.156716833, 1.156716833,
            ...Array(97).fill(0),

            0.89845625, 0.89845625, 0.89845625,
            ...Array(97).fill(0)
        ],

        aadResponse2: [],

        xG1Spline: {
            sSplineNodeCount: 5,
            afX: [0.63, 0.932432, 1.177027, 1.35, 1.42],
            afY1: [4.224174, 1.854604, 0.679804, -0.013477, -0.292221],
            afY2: [0.0, 16.426641, 0.900763, 0.0, 0.0]
        },

        xG2Spline: {
            sSplineNodeCount: 5,
            afX: [0.63, 0.932432, 1.177027, 1.35, 1.42],
            afY1: [4.224174, 1.854604, 0.679804, -0.013477, -0.292221],
            afY2: [0.0, 16.426641, 0.900763, 0.0, 0.0]
        }
    });
    
    // Build payload for a single sample point. Predict is called once per row
    // by callPredict (matches old DMController which loops predictRequest.Points).
    const buildPredictPayloadFor = (row) => ({
        input: {
            dResponse1: num(row?.response1),
            dResponse2: num(row?.response2),
            dFirstDr: 0,
            dSubDepCheckValue: 0,
            dSubDepDeltaCheckValue: 0,
            dBlankConcentration: 0
        },
        assay: {
            ...buildAssay(),
            fCalInterceptA0: num(parameters?.fCalInterceptA0),
            fCalSlopeA1: num(parameters?.fCalSlopeA1, 1),
            fCalCurvatureA2: num(parameters?.fCalCurvatureA2),
            fCalBlankCorrectionFactorA3: num(parameters?.fCalBlankCorrectionFactorA3)
        }
    });

    // ------------------------------------------------------------------
    // Calls
    // ------------------------------------------------------------------
    // Two retries with exponential backoff cover Lambda cold starts (5-15s)
    // without surfacing the failure to the user. Pages/MicroSlide.jsx also
    // fires a /health ping on mount to warm the function in advance.
    const RETRY_DELAYS_MS = [2000, 4000];
    const fetchWithRetry = async (url, init) => {
        let lastErr;
        for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
            try {
                return await fetch(url, init);
            } catch (err) {
                lastErr = err;
                if (attempt < RETRY_DELAYS_MS.length) {
                    await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
                }
            }
        }
        throw lastErr;
    };

    const call = async (endpoint, payload) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchWithRetry(
                API_BASE_URL + "api/v1/microslide/" + endpoint,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                }
            );
            const data = await res.json();
            setResponse(data);
            setError(null);
            return data;
        } catch (err) {
            const msg = err?.message === "Failed to fetch"
                ? "Backend unreachable (Lambda cold start or AWS edge issue). Wait a few seconds and retry."
                : (err?.message || "Network error");
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const callResponse = () => call("response", buildResponsePayload());
    const callCalibrate = () => call("calibrate", buildCalibratePayload());

    // Predict batches client-side, one POST per sample row (matches old
    // DMController.PredictDualWavelength which loops predictRequest.Points).
    const callPredict = async () => {
        const cleanRows = (rows || []).filter(r =>
            r && (r.response1 !== "" && r.response1 !== undefined)
        );
        if (cleanRows.length === 0) {
            setError("No sample rows with Response 1 to predict");
            return null;
        }

        setLoading(true);
        setError(null);
        try {
            const predictions = [];
            for (const row of cleanRows) {
                const res = await fetchWithRetry(
                    API_BASE_URL + "api/v1/microslide/predict",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(buildPredictPayloadFor(row))
                    }
                );
                const data = await res.json();
                predictions.push({
                    sampleId: row.sampleId ?? "",
                    response1: row.response1,
                    response2: row.response2,
                    concentration: data?.dConcentration ?? null,
                    conditionCode: data?.zConditionCode ?? "",
                    error: data?.zError ?? ""
                });
            }
            const result = { predictions };
            setResponse(result);
            setError(null);
            return result;
        } catch (err) {
            const msg = err?.message === "Failed to fetch"
                ? "Backend unreachable mid-batch. Wait a few seconds and retry."
                : (err?.message || "Network error");
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    };

    return { callResponse, callCalibrate, callPredict, response, loading, error };
}
